import { useEffect, useState } from "react";
import useMemoizedSelector from "@shared/utils/hooks/useMemoizedSelector";
import { GetExchangeSignalByIdRequest } from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { OrderStatus } from "@shared/types";
import {
  getFormattedTargets,
  isEditableTargetZone,
} from "@shared/features/signals/common/utils";
import {
  EditTargetData,
  EditTargetOrZone,
} from "@shared/features/signals/EditSignal/types";
import { sharedLib } from "@shared/services/shared.service";
import { redistributeOrdersWithAddedRatio } from "@shared/features/trades/common/utils";
import {
  getTargetsBreakEvenPrice,
  isPartiallyFulfilledZone,
  moveZoneBorderToPriceReached,
} from "@shared/features/signals/EditSignal/utils";

interface useEditSignalTargetsProps {
  targetsArgs?: GetExchangeSignalByIdRequest;
  onValuesModified?: () => void;
  isShort?: boolean;
  isBreakout?: boolean;
}

export const useEditSignalTargets = ({
  targetsArgs,
  onValuesModified,
  isShort,
  isBreakout,
}: useEditSignalTargetsProps) => {
  const { data: targetsData } = useMemoizedSelector({
    selectorCreator:
      sharedLib.sharedApi.signals.endpoints.getSignalExtraInfoTargets.select,
    args: targetsArgs,
  });

  const [entryTargets, setEntryTargets] = useState<EditTargetOrZone[]>([]);
  const [takeProfitTargets, setTakeProfitTargets] = useState<
    EditTargetOrZone[]
  >([]);
  const [stopTargets, setStopTargets] = useState<EditTargetOrZone[]>([]);
  const currentPrice = targetsData?.data.current_price;

  const resetTargetsChanges = () => {
    const targets = getFormattedTargets(targetsData?.data, isShort, isBreakout);
    if (targets) {
      setEntryTargets(targets.entryTargets);
      setTakeProfitTargets(targets.takeProfitTargets);
      setStopTargets(targets.stopTargets);
    }
  };

  useEffect(() => {
    resetTargetsChanges();
  }, [targetsData]);

  const updateTarget = (
    targetType: OrderTypes,
    targetIndex: number,
    updatedTarget: EditTargetOrZone
  ) => {
    if (targetType === OrderTypes.ENTRY) {
      const updatedTargets = [...entryTargets];
      updatedTargets[targetIndex] = updatedTarget;
      setEntryTargets(updatedTargets);
    } else if (targetType === OrderTypes.TAKE_PROFIT) {
      const updatedTargets = [...takeProfitTargets];
      updatedTargets[targetIndex] = updatedTarget;
      setTakeProfitTargets(updatedTargets);
    } else if (targetType === OrderTypes.STOP) {
      const updatedTargets = [...stopTargets];
      updatedTargets[targetIndex] = updatedTarget;
      setStopTargets(updatedTargets);
    }
    onValuesModified?.();
  };

  const addTarget = (targetType: OrderTypes, target: EditTargetData) => {
    let updatedTargets = [...getTargetsByType(targetType), target];
    setTargetsByType(targetType, updatedTargets);
  };

  const getTargetsByType = (targetType: OrderTypes): EditTargetOrZone[] => {
    if (targetType === OrderTypes.ENTRY) {
      return [...entryTargets];
    } else if (targetType === OrderTypes.TAKE_PROFIT) {
      return [...takeProfitTargets];
    } else {
      return [...stopTargets];
    }
  };

  const setTargetsByType = (
    targetType: OrderTypes,
    targets: EditTargetOrZone[]
  ) => {
    if (targetType === OrderTypes.ENTRY) {
      setEntryTargets(targets);
    } else if (targetType === OrderTypes.TAKE_PROFIT) {
      setTakeProfitTargets(targets);
    } else {
      setStopTargets(targets);
    }
    onValuesModified?.();
  };

  const updateTargetPrice = (
    targetType: OrderTypes,
    targetIndex: number,
    price: number | [number, number]
  ) => {
    const updatedTargets = getTargetsByType(targetType);
    const target = updatedTargets[targetIndex];
    if (isEditableTargetZone(target)) {
      if (Array.isArray(price)) {
        updatedTargets[targetIndex] = {
          ...target,
          edit_target_price: [price[0].toString(), price[1].toString()],
        };
      }
    } else {
      updatedTargets[targetIndex] = {
        ...target,
        edit_target_price: price.toString(),
      };
    }

    setTargetsByType(targetType, updatedTargets);
    onValuesModified?.();
  };

  const cancelTarget = (
    _: OrderTypes,
    targetIndex: number,
    shouldRedistribute: boolean,
    targets: EditTargetOrZone[]
  ) => {
    let updatedTargets = [...targets];
    const updateOrRemoveTarget = () => {
      const isNewlyAddedTarget = !isEditableTargetZone(target) && !target.id;
      if (isNewlyAddedTarget) updatedTargets.splice(targetIndex, 1);
      else updatedTargets.splice(targetIndex, 1, target);
    };

    let target: EditTargetOrZone = { ...targets[targetIndex] };
    if (!isEditableTargetZone(target)) {
      if (target.id) {
        target.edit_status = OrderStatus.CANCELLED;
      }
      const removedRatio = target.edit_ratio || 0;

      if (shouldRedistribute && removedRatio) {
        updatedTargets = redistributeOrdersWithAddedRatio(
          updatedTargets as EditTargetData[],
          removedRatio,
          target.id ? targetIndex : undefined
        );
      }
      target.edit_ratio = typeof target.edit_ratio === "number" ? 0 : null;
    } else if (isPartiallyFulfilledZone(target)) {
      moveZoneBorderToPriceReached(target, Boolean(isShort));
    }

    updateOrRemoveTarget();
    return updatedTargets;
  };

  const moveTargetToCurrentPrice = (
    targetType: OrderTypes,
    targetIndex: number
  ) => updateTargetPrice(targetType, targetIndex, currentPrice!);

  const moveStopTargetToBreakEvenPrice = (
    entryTargetsToCancel: EditTargetOrZone[]
  ) => {
    let allEntryTargets = getTargetsByType(OrderTypes.ENTRY);
    const breakEvenPrice = getTargetsBreakEvenPrice(allEntryTargets, isShort);
    const targetIndex = stopTargets.findIndex(
      (target) => target.edit_status === OrderStatus.PENDING
    );
    updateTargetPrice(OrderTypes.STOP, targetIndex, breakEvenPrice);
    entryTargetsToCancel.forEach((target) => {
      const targetIndex = allEntryTargets.indexOf(target);
      if (targetIndex !== -1) {
        allEntryTargets = cancelTarget(
          OrderTypes.ENTRY,
          targetIndex,
          false,
          allEntryTargets
        );
      }
    });
    setEntryTargets(allEntryTargets);
  };

  return {
    entryTargets,
    takeProfitTargets,
    stopTargets,
    targetsData,
    setEntryTargets,
    setTakeProfitTargets,
    setStopTargets,
    setTargetsByType,
    resetTargetsChanges,
    updateTarget,
    getTargetsByType,
    moveTargetToCurrentPrice,
    addTarget,
    cancelTarget,
    moveStopTargetToBreakEvenPrice,
  };
};
